import { useEffect, useRef, useState } from 'react';
import {
  useDeletePronunciationMutation,
  useGetPronunciationsQuery,
  useGetTestVoicesQuery,
  useSavePronunciationMutation,
  useTestPronunciationMutation,
  type PronunciationTestResult
} from '@/store/configApi';
import { useActiveLocation } from '@/hooks/useActiveLocation';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { LocationPicker } from '@/components/layout/LocationPicker';
import { Button, EmptyState, Panel, SkeletonRows } from '@/components/ui';

/**
 * Type a word, hear it exactly as a caller would, and fix it if it is wrong.
 *
 * The audio comes from the phone line's own voice with the saved
 * pronunciations applied, so what plays here is what plays on a call. A fix
 * saved here is used from the next call on.
 */

const EXAMPLES = [
  { display: 'Chaat', spoken: 'chaaht' },
  { display: 'Dahi Bhalle', spoken: 'duh-hee bhul-lay' },
  { display: 'Chole Bhature', spoken: 'cho-lay bha-too-ray' },
  { display: 'Paneer', spoken: 'puh-neer' }
];

export function PronunciationTestPage() {
  const { locationId } = useActiveLocation();
  const dispatch = useAppDispatch();

  const voices = useGetTestVoicesQuery();
  const { data: dictionary, isLoading } = useGetPronunciationsQuery(locationId, { skip: !locationId });
  const [test, testing] = useTestPronunciationMutation();
  const [save, saving] = useSavePronunciationMutation();
  const [remove] = useDeletePronunciationMutation();

  const [voiceId, setVoiceId] = useState('');
  const [word, setWord] = useState('');
  const [fix, setFix] = useState('');
  const [heard, setHeard] = useState<{ label: string; result: PronunciationTestResult } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!voiceId && voices.data) setVoiceId(voices.data.defaultVoiceId);
  }, [voices.data, voiceId]);

  const play = async (text: string, label: string, trySpoken?: string) => {
    if (!text.trim() || !locationId) return;
    const result = await test({ locationId, text: text.trim(), voiceId: voiceId || undefined, trySpoken });
    if ('error' in result) {
      const status = (result.error as { status?: number }).status;
      dispatch(
        pushToast(
          status === 503
            ? 'The line’s voice is not reachable from the dashboard right now.'
            : 'Could not play that. Try again.',
          'error'
        )
      );
      return;
    }
    setHeard({ label, result: result.data });
    audioRef.current?.pause();
    const audio = new Audio(`data:${result.data.contentType};base64,${result.data.audio}`);
    audioRef.current = audio;
    await audio.play().catch(() => undefined);
  };

  const listen = async () => {
    await play(word, 'As a caller hears it now');
  };

  const tryFix = async () => {
    if (!fix.trim()) {
      dispatch(pushToast('Write how it should be said first.', 'error'));
      return;
    }
    await play(word, 'With your fix', fix.trim());
  };

  const saveFix = async () => {
    if (!word.trim() || !fix.trim()) {
      dispatch(pushToast('Both the word and how it is said are needed.', 'error'));
      return;
    }
    const result = await save({ locationId, display: word.trim(), spoken: fix.trim() });
    if ('error' in result) {
      dispatch(pushToast('Could not save that pronunciation.', 'error'));
      return;
    }
    dispatch(pushToast(`Saved. Calls will say “${word.trim()}” this way from the next call.`, 'success'));
  };

  const loadWord = (display: string, spoken: string) => {
    setWord(display);
    setFix(spoken);
    setHeard(null);
  };

  const gemini = voices.data?.provider === 'gemini';
  const terms = dictionary?.terms ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-display">Test pronunciation</h1>
        <LocationPicker />
      </div>

      {gemini ? (
        <EmptyState
          headline="Not available with this voice"
          hint="The line speaks through Gemini Live, which has no separate voice to test or correct."
        />
      ) : (
        <Panel title="Hear a word">
          <div className="space-y-4 px-4 py-4">
            <p className="max-w-2xl text-small text-slate">
              Type a dish or any word and press Listen. You hear the same voice callers hear. If it
              sounds wrong, write it the way it should sound — in simple English syllables — try it,
              and save it. From the next call on, the line says it that way.
            </p>

            <div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
              <label className="block space-y-1.5">
                <span className="block text-small font-medium text-ink">Word or sentence</span>
                <input
                  value={word}
                  onChange={(event) => {
                    setWord(event.target.value);
                    setHeard(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void listen();
                  }}
                  placeholder="e.g. Dahi Bhalle"
                  maxLength={200}
                  className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
                />
              </label>
              <label className="block space-y-1.5">
                <span className="block text-small font-medium text-ink">Voice</span>
                <select
                  value={voiceId}
                  onChange={(event) => setVoiceId(event.target.value)}
                  className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
                >
                  {(voices.data?.data ?? []).map((voice) => (
                    <option key={voice.voiceId} value={voice.voiceId}>
                      {voice.name} · {voice.accent}
                      {voice.voiceId === voices.data?.defaultVoiceId ? ' (phone calls)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <Button loading={testing.isLoading} disabled={!word.trim()} onClick={listen}>
              Listen
            </Button>

            {heard ? (
              <div className="rounded border border-rule bg-paper px-3 py-2.5 text-small">
                <p className="text-slate">{heard.label}</p>
                <p className="mt-0.5 text-ink">
                  The voice was given: <span className="font-medium">“{heard.result.spokenText}”</span>
                </p>
                {heard.result.savedSpoken && heard.label !== 'With your fix' ? (
                  <p className="mt-0.5 text-micro text-slate">
                    A saved fix is already in use: “{heard.result.savedSpoken}”.
                  </p>
                ) : null}
                <button
                  onClick={() => void audioRef.current?.play().catch(() => undefined)}
                  className="mt-1 text-small text-jade underline underline-offset-2"
                >
                  Play again
                </button>
              </div>
            ) : null}

            <div className="border-t border-rule pt-4">
              <label className="block space-y-1.5">
                <span className="block text-small font-medium text-ink">How it should sound</span>
                <input
                  value={fix}
                  onChange={(event) => setFix(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void tryFix();
                  }}
                  placeholder="e.g. duh-hee bhul-lay"
                  maxLength={200}
                  className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
                />
              </label>
              <p className="mt-1.5 text-micro text-slate">
                Tips: split into syllables with hyphens, spell long sounds out (“aa”, “ee”, “oo”), and
                try a few spellings until it sounds right. Examples:{' '}
                {EXAMPLES.map((example, index) => (
                  <span key={example.display}>
                    {index > 0 ? ', ' : ''}
                    <button
                      onClick={() => loadWord(example.display, example.spoken)}
                      className="text-jade underline underline-offset-2"
                    >
                      {example.display} → {example.spoken}
                    </button>
                  </span>
                ))}
                .
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  loading={testing.isLoading}
                  disabled={!word.trim() || !fix.trim()}
                  onClick={tryFix}
                >
                  Try this spelling
                </Button>
                <Button loading={saving.isLoading} disabled={!word.trim() || !fix.trim()} onClick={saveFix}>
                  Save fix
                </Button>
              </div>
            </div>
          </div>
        </Panel>
      )}

      <Panel title="Saved fixes">
        {isLoading ? (
          <SkeletonRows rows={3} />
        ) : terms.length === 0 ? (
          <EmptyState
            headline="No fixes yet"
            hint="Words you correct above appear here and are used on every call."
          />
        ) : (
          <ul className="divide-y divide-rule">
            {terms.map((term) => (
              <li key={term._id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <span className="text-small text-ink">{term.display}</span>
                <span className="text-small text-slate">said as “{term.spoken}”</span>
                <div className="ml-auto flex gap-3">
                  <button
                    onClick={() => void play(term.display, 'As a caller hears it now')}
                    className="text-small text-jade underline underline-offset-2"
                  >
                    Listen
                  </button>
                  <button
                    onClick={() => loadWord(term.display, term.spoken)}
                    className="text-small text-slate hover:text-ink"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(term._id)}
                    className="text-small text-slate hover:text-rust"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
